import type { SeedComplianceStatus, SeedDefinition } from './consumerContractRegistry.types';

export const TRICKLE_VENTS_SEED_KEY = 'trickle_vents';
export const EXTENDERS_MM_SEED_KEY = 'extenders_mm';

/** Registry fieldKey → seedKey. Only governed fields appear here. */
export const FIELD_KEY_TO_SEED_KEY: Record<string, string> = {
  trickle_vents: TRICKLE_VENTS_SEED_KEY,
  extenders_mm: EXTENDERS_MM_SEED_KEY,
};

export const SEED_DEFINITIONS: SeedDefinition[] = [
  {
    seedKey: TRICKLE_VENTS_SEED_KEY,
    fieldKeys: ['trickle_vents'],
    meaning:
      'Whether trickle vents are fitted and, when enabled, which structural location they occupy on the window line.',
    allowedValues: {
      observed: ['sash', 'head', 'custom', 'extender'],
      target: ['sash', 'head', 'custom', 'extender'],
      labels: {
        sash: 'Head of Sash (In Sash)',
        head: 'Head Jamb (In Head)',
        custom: 'Custom per-pane placement',
        extender: 'Extender (top frame extender; render uses jamb ventPos)',
      },
    },
    semanticAuthority: {
      path: 'meta_json.specs.options.trickle_vents_position',
      companionPaths: ['meta_json.specs.options.trickle_vents'],
      domainOwner: 'canonical_window',
    },
    projections: [
      {
        path: 'meta_json.window_style.templatePayload.presentation.ventPos',
        role: 'render',
        authoritativeForDisplay: false,
        forwardMapping:
          'mergeModalStateIntoPayload: no→none; custom→none+preserve paneVents; head→jamb; extender→extender (jamb-like render); sash→sash',
        reverseHydration:
          'deriveFromWindowGeneratorPayload: ventPos extender→extender; jamb|pane jamb→head; sash|pane sash→sash; custom paneVents→custom',
        vocabularyNote: 'Semantic head maps to render ventPos jamb — different vocabulary, same observed pair today.',
      },
      {
        path: 'meta_json.window_style.templatePayload.presentation.paneVents',
        role: 'render',
        authoritativeForDisplay: false,
        forwardMapping: 'Preserved on custom; preset modes use explicit false overrides for non-selected panes',
        reverseHydration: 'Custom detection when ventPos custom or any pane override / _centre present',
      },
    ],
    workflowRules: [
      {
        workflow: 'VD modal → VD Save (accepted line)',
        direction: 'semantic_establishes',
        notes:
          'Modal writes trickle_vents + trickle_vents_position to specs.options and merged templatePayload in one meta assembly (modal save handler).',
      },
      {
        workflow: 'WG-first / payload hydrate → modal open',
        direction: 'projection_establishes',
        notes:
          'deriveFromWindowGeneratorPayload infers semantic position from presentation before user accepts (and on edit hydrate).',
      },
      {
        workflow: 'After VD Save acceptance',
        direction: 'semantic_wins_after_acceptance',
        notes:
          'Target contract: semantic path is established; render projection must follow forward — not re-infer semantic from projection.',
      },
      {
        workflow: 'Quote Save',
        direction: 'semantic_wins_after_acceptance',
        notes:
          'Target: persist accepted line meta unchanged. Safeguard is persistence fidelity, not remapping. Must not become a third authoring path.',
      },
    ],
    observed_current: {
      summary:
        'Semantic and render representations both live in meta_json. VD Save writes both in one handler. Reverse hydration from payload exists on reopen/WG-first flows. Precedence after acceptance is not enforced in code — bidirectional inference possible.',
      precedenceOnDisagreement:
        'Observed: no explicit guard — deriveFromWindowGeneratorPayload can repopulate modal from projection; getDesignerSummaryRows reads semantic path only.',
      authoringSurfaces: [
        'Visual Designer modal trickle vents Select (QuoteVisualDesignerCard.tsx)',
        'Window Generator session (payload presentation)',
        'Bulk edit overlay (QuoteVisualDesignerCard.tsx)',
        'Legacy merge builders (visualDesignerPayloadResolution.ts — non-authoritative for v2_lite saved payload)',
      ],
      writeInvariants: [
        {
          writePoint: 'vd_save_accepted',
          rule: 'commitLines receives meta with specs.options.trickle_vents_position and merged templatePayload together (modal save handler → commitLines).',
          safeguard: 'Integration tests on save assembly; manual reopen parity.',
        },
        {
          writePoint: 'quote_save_persisted',
          rule: 'saveCurrentVersion persists document lines — observed path does not independently remap trickle fields (EstimateQuoteWizard.tsx).',
          safeguard: 'Persistence fidelity — accepted meta_json round-trips to DB unchanged.',
        },
      ],
    },
    target_contract: {
      summary:
        'Single semantic authority after VD Save. Forward projection to ventPos/paneVents only. Reverse hydration permitted pre-acceptance or when semantic absent — never overrides accepted semantic.',
      precedenceOnDisagreement: 'Semantic specs.options wins after VD Save; projection follows via forward mapping only.',
      authoringSurfaces: [
        'Visual Designer modal (semantic source on user edit)',
        'Window Generator (projection source pre-acceptance only)',
        'VD Save (atomic semantic + projection assembly)',
      ],
      writeInvariants: [
        {
          writePoint: 'vd_save_accepted',
          rule: 'Atomically assemble semantic value and render projection into accepted line meta — one truth established.',
          safeguard: 'Seed contract test: both paths present and forward mapping consistent.',
        },
        {
          writePoint: 'quote_save_persisted',
          rule: 'Persist accepted line unchanged — no independent derive or rebuild of semantic/projection pair.',
          safeguard: 'Persistence fidelity test — DB meta_json equals accepted in-memory meta.',
        },
      ],
    },
    compliance_status: 'partially_aligned',
    complianceNotes:
      'VD Save atomic write observed. extender semantic value implemented; render projection ventPos extender uses jamb-like vent behaviour; selecting extender sets ext_top_mm ≥ 20 without clearing on position change.',
    codeReferences: [
      'QuoteVisualDesignerCard.tsx#mergeModalStateIntoPayload',
      'QuoteVisualDesignerCard.tsx#deriveFromWindowGeneratorPayload',
      'visualDesignerSummary.ts#getDesignerSummaryRows',
      'QuoteVisualDesignerCard.tsx#save (VD Save)',
      'visualDesignerPayloadResolution.ts',
    ],
    pendingValues: [],
  },
  {
    seedKey: EXTENDERS_MM_SEED_KEY,
    fieldKeys: ['extenders_mm'],
    meaning: 'Frame extender depths in mm on each side (top, bottom, left, right) of the window line.',
    allowedValues: {
      observed: ['non-negative integers per side; all-zero means none'],
      target: ['non-negative integers per side; all-zero means none'],
      labels: {},
    },
    semanticAuthority: {
      path: 'meta_json.specs.options.ext_top_mm, ext_bottom_mm, ext_left_mm, ext_right_mm',
      domainOwner: 'canonical_window',
    },
    projections: [
      {
        path: 'meta_json.window_style.templatePayload.presentation.extTop, extBot, extLeft, extRight',
        role: 'render',
        authoritativeForDisplay: false,
        forwardMapping: 'mergeModalStateIntoPayload writes presentation ext* from modal ext*Mm (QuoteVisualDesignerCard.tsx)',
        reverseHydration: 'deriveFromWindowGeneratorPayload reads presentation ext* back to extras.ext_*_mm',
        vocabularyNote: 'Same numeric mm values; presentation keys use extTop not ext_top_mm.',
      },
    ],
    workflowRules: [
      {
        workflow: 'VD modal → VD Save',
        direction: 'semantic_establishes',
        notes: 'Modal extTopMm…extRightMm → specs.options.ext_*_mm and merged templatePayload presentation.',
      },
      {
        workflow: 'WG / VD-via-WG',
        direction: 'projection_establishes',
        notes: 'Pre-acceptance: deriveFromWindowGeneratorPayload may hydrate modal from presentation ext*.',
      },
      {
        workflow: 'After VD Save',
        direction: 'semantic_wins_after_acceptance',
        notes: 'Display uses formatExtendersMmFromOptions on specs.options — not notes mirror.',
      },
    ],
    observed_current: {
      summary:
        'Dedicated Extenders (mm) row via getDesignerSummaryRows and formatExtendersMmFromOptions. Previously also auto-appended to specs.notes — removed. Public PDF edge now maps item.extenders from options.',
      precedenceOnDisagreement:
        'Display branches read specs.options.ext_*_mm. Notes auto-mirror of extenders removed; stripAutoDerivedNotes still cleans legacy saved notes.',
      authoringSurfaces: [
        'Visual Designer modal T/B/L/R inputs (QuoteVisualDesignerCard.tsx)',
        'Window Generator presentation ext*',
        'Bulk edit overlay',
      ],
      writeInvariants: [
        {
          writePoint: 'vd_save_accepted',
          rule: 'specs.options.ext_*_mm and presentation ext* written together; extenders not copied into notes.',
          safeguard: 'formatExtendersMmFromOptions used by summary rows; no extenders segment in autoNotes.',
        },
        {
          writePoint: 'quote_save_persisted',
          rule: 'Persist accepted meta_json unchanged.',
          safeguard: 'Persistence fidelity.',
        },
      ],
    },
    target_contract: {
      summary:
        'Single global display field Extenders (mm) from specs.options via formatExtendersMmFromOptions; all structured artifacts reference extenders_mm; notes never carry extenders.',
      precedenceOnDisagreement: 'Semantic ext_*_mm wins after VD Save; projection follows forward.',
      authoringSurfaces: ['Visual Designer modal', 'Window Generator', 'VD Save atomic assembly'],
      writeInvariants: [
        {
          writePoint: 'vd_save_accepted',
          rule: 'Semantic + projection atomic; no notes mirror.',
          safeguard: 'Contract test: summary row present, notes exclude extender auto-segment.',
        },
        {
          writePoint: 'quote_save_persisted',
          rule: 'Persistence fidelity only.',
          safeguard: 'Round-trip meta_json equality.',
        },
      ],
    },
    compliance_status: 'aligned',
    complianceNotes:
      'Global formatter shared by getDesignerSummaryRows, staff PDF legacy flat path, and public-doc edge mapper. Notes auto-mirror removed.',
    codeReferences: [
      'src/modules/docs/utils/visualDesignerFieldFormatters.ts#formatExtendersMmFromOptions',
      'visualDesignerSummary.ts#getDesignerSummaryRows',
      'DocumentDetailPage.tsx#renderTemplate',
      'supabase/functions/public-doc/index.ts#visualDesignerItems',
    ],
  },
];

const seedByKey = new Map(SEED_DEFINITIONS.map((s) => [s.seedKey, s]));

export function resolveSeedKeyForField(fieldKey: string): string | undefined {
  return FIELD_KEY_TO_SEED_KEY[fieldKey];
}

export function getSeedDefinition(seedKey: string): SeedDefinition | undefined {
  return seedByKey.get(seedKey);
}

export function getSeedForField(fieldKey: string): SeedDefinition | undefined {
  const seedKey = resolveSeedKeyForField(fieldKey);
  return seedKey ? getSeedDefinition(seedKey) : undefined;
}

export function labelForSeedCompliance(status: SeedComplianceStatus): string {
  switch (status) {
    case 'aligned':
      return 'Aligned';
    case 'partially_aligned':
      return 'Partially aligned';
    case 'conflicting':
      return 'Conflicting';
    case 'unknown':
      return 'Unknown';
    default:
      return status;
  }
}

export function complianceColor(
  status: SeedComplianceStatus,
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'aligned':
      return 'success';
    case 'partially_aligned':
      return 'warning';
    case 'conflicting':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function validateSeedReferences(
  subscriptions: Array<{ id: string; seedKey?: string; fieldKey: string }>,
): Array<{ path: string; message: string }> {
  const issues: Array<{ path: string; message: string }> = [];
  for (const sub of subscriptions) {
    if (!sub.seedKey) continue;
    const seed = getSeedDefinition(sub.seedKey);
    if (!seed) {
      issues.push({ path: sub.id, message: `Unknown seedKey "${sub.seedKey}"` });
      continue;
    }
    if (!seed.fieldKeys.includes(sub.fieldKey)) {
      issues.push({
        path: sub.id,
        message: `seedKey "${sub.seedKey}" does not govern fieldKey "${sub.fieldKey}"`,
      });
    }
  }
  return issues;
}
